import { motion } from "framer-motion";
import Parallax from "./Parallax";

const SECTORS = [
  { name: "Technology & SaaS", desc: "Product, engineering, data, and platform teams operating in step with your global roadmap.", img: "/assets/img/sector-tech.jpg" },
  { name: "Creative studios & design agencies", desc: "Designers and craft leads working within your brand guidelines, review rhythms, and quality bar.", img: "/assets/img/sector-creative.jpg" },
  { name: "Marketing, digital & social agencies", desc: "Content, media, performance, and community teams built into your delivery model.", img: "/assets/img/sector-marketing.jpg" },
  { name: "Production companies", desc: "Post-production, motion, and content operations set up for the way your studios actually work.", img: "/assets/img/sector-production.jpg" },
  { name: "Professional services firms", desc: "Research, analysis, and delivery capability positioned inside your practice and standards.", img: "/assets/img/sector-professional.jpg" },
];

export default function Sectors() {
  return (
    <section id="sectors" className="relative -mt-8 rounded-t-[48px] py-20 sm:py-28 lg:py-32 bg-white" data-testid="sectors-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-3xl mb-14"
        >
          <span className="inline-flex items-center rounded-full border border-[#5B4199]/40 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#5B4199] mb-6">
            Who we support
          </span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-7xl font-extrabold tracking-tight text-[#5B4199] leading-[1.04]">
            Capabilities for teams across sectors.
          </h2>
          <p className="mt-6 text-base sm:text-lg text-[#475569] leading-relaxed">
            India teams aren't just for engineering. We help creative, marketing, production, and
            professional services teams build a presence that fits how they actually work.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {SECTORS.map((s, i) => (
            <Parallax key={s.name} distance={i % 3 === 0 ? 30 : i % 3 === 1 ? -22 : 40} className="h-full">
              <motion.div
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.07, ease: [0.22, 1, 0.36, 1] }}
                className={`card-jeton group h-full rounded-[28px] bg-[#F6F3FE] shadow-[0_24px_56px_-18px_rgba(76,29,149,0.2)] overflow-hidden hover:rotate-0 ${
                  i % 2 === 0 ? "lg:-rotate-1" : "lg:rotate-1"
                }`}
                data-testid={`sector-card-${i}`}
              >
                <div className="h-44 overflow-hidden">
                  <img
                    src={s.img}
                    alt={s.name}
                    className="w-full h-full object-cover object-[center_20%] transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
                <div className="p-7">
                  <h3 className="font-display text-lg font-extrabold text-[#0B132B] mb-2.5 leading-snug">{s.name}</h3>
                  <p className="text-sm text-[#475569] leading-relaxed">{s.desc}</p>
                </div>
              </motion.div>
            </Parallax>
          ))}
          <Parallax distance={-30} className="h-full">
            <motion.div
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="card-jeton h-full rounded-[28px] bg-[#5B4199] shadow-[0_28px_60px_-18px_rgba(91,65,153,0.5)] p-8 flex flex-col justify-between lg:rotate-1 hover:rotate-0"
              data-testid="sector-card-note"
            >
              <p className="font-display text-lg font-bold text-white leading-snug">
                Don't see your sector? If the work can be done brilliantly from India,
                we build the team around how you operate.
              </p>
              <a href="#contact" className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-white/90 hover:text-white transition-colors">
                Talk to us about your sector →
              </a>
            </motion.div>
          </Parallax>
        </div>
      </div>
    </section>
  );
}
